import React from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { jobStatusLabels, jobStatusIcons } from 'constants/jobs';
import colors from 'styles/colors';
import Box from '@mui/material/Box';

const StatusMultiSelect = ({ selectedStatuses, onChange }) => {
  const statusOptions = Object.entries(jobStatusLabels).map(([value, label]) => ({
    value,
    label,
  }));

  const allStatusValues = statusOptions.map((option) => option.value);

  // Style for selected menu items
  const selectedItemStyle = {
    '&.Mui-selected': {
      backgroundColor: 'rgba(0, 96, 250, 0.08)', // #0060FA14
    },
    '&.Mui-selected:hover': {
      backgroundColor: 'rgba(0, 96, 250, 0.12)', // Slightly darker on hover
    },
  };

  const handleChange = (event) => {
    const selectedValues = event.target.value;

    // Handle "Select All" option
    if (selectedValues.includes('all')) {
      // If "all" is clicked and all options are already selected or in intermediate state, deselect all
      if (allSelected || someSelected) {
        onChange(null);
      } else {
        // Otherwise, select all options
        onChange(allStatusValues);
      }
    } else {
      onChange(selectedValues.length ? selectedValues : null);
    }
  };

  // Determine if all options are selected
  const allSelected = selectedStatuses && allStatusValues.every((value) => selectedStatuses.includes(value));

  // Determine if some but not all options are selected (for indeterminate state)
  const someSelected = selectedStatuses && selectedStatuses.length > 0 && !allSelected;

  return (
    <FormControl fullWidth>
      <InputLabel id="status-filter-label">Status</InputLabel>
      <Select
        variant="outlined"
        labelId="status-filter-label"
        id="status-filter"
        multiple
        value={selectedStatuses || []}
        onChange={handleChange}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {selected.map((value) => {
              const Icon = jobStatusIcons[value];
              return (
                <Box key={value} sx={{ display: 'flex', alignItems: 'center' }}>
                  {Icon && <Icon marginRight="5px" />}
                  {statusOptions.find((option) => option.value === value)?.label}
                  {value !== selected[selected.length - 1] && ', '}
                </Box>
              );
            })}
          </Box>
        )}
        sx={{
          '.MuiOutlinedInput-notchedOutline': {
            borderRadius: '8px',
          },
          '.MuiSelect-icon': {
            color: colors.text.primary,
            background: colors.background.paper_5p,
            right: '0px',
          },
        }}
      >
        {/* Select All option */}
        <MenuItem
          key="all"
          value="all"
          selected={allSelected || someSelected}
          sx={{
            ...selectedItemStyle,
            '&[aria-selected="false"]': {
              backgroundColor: allSelected || someSelected ? 'rgba(0, 96, 250, 0.08)' : 'transparent',
            },
          }}
        >
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
          />
          <ListItemText primary="Select All" />
        </MenuItem>

        {/* Divider between Select All and other options */}
        <MenuItem
          disabled
          sx={{
            borderBottom: `1px solid ${colors.border.border_10p}`,
            margin: '4px 0',
            padding: 0,
            pointerEvents: 'none',
          }}
        />

        {statusOptions.map((status) => {
          const isSelected = (selectedStatuses || []).indexOf(status.value) > -1;
          const Icon = jobStatusIcons[status.value];
          return (
            <MenuItem
              key={status.value}
              value={status.value}
              selected={isSelected}
              sx={{
                ...selectedItemStyle,
                '&[aria-selected="false"]': {
                  backgroundColor: isSelected ? 'rgba(0, 96, 250, 0.08)' : 'transparent',
                },
              }}
            >
              <Checkbox checked={isSelected} />
              {jobStatusIcons[status.value] && <Icon marginRight="9px" />}
              <ListItemText primary={status.label} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default StatusMultiSelect;
